import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Check,
  X,
  Clock,
  Building2,
  ClipboardList,
  User,
  AlertTriangle,
  CheckCircle2,
  Unlock,
  Pencil,
  Camera,
  ChevronDown,
  ChevronUp,
  Trash2,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { getWorkTypeColor, getWorkTypeLabel } from "@/lib/workTypeConstants";
import { JornadaDocumentos } from "./JornadaDocumentos";

export interface JornadaDetalle {
  id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string | null;
  horas_trabajadas: number | null;
  tipo_trabajo: string;
  descripcion_trabajo: string | null;
  es_hora_extra: boolean;
  estado_validacion: 'pendiente' | 'aprobada' | 'rechazada';
  motivo_rechazo: string | null;
  tecnico?: { nombre: string; email: string } | null;
  obra?: { nombre: string } | null;
  orden_trabajo?: { numero_ot: string; titulo: string } | null;
  cliente?: { nombre: string } | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jornadas: JornadaDetalle[];
  canValidate: boolean;
  onAprobar: (j: JornadaDetalle) => Promise<void> | void;
  onRechazar: (j: JornadaDetalle, motivo: string) => Promise<void> | void;
  onReabrir?: (j: JornadaDetalle) => Promise<void> | void;
  onEditar?: (j: JornadaDetalle) => void;
  onEliminar?: (j: JornadaDetalle) => Promise<void> | void;
  loading?: boolean;
}

const EstadoBadge: React.FC<{ estado: JornadaDetalle["estado_validacion"] }> = ({ estado }) => {
  if (estado === 'aprobada')
    return (
      <Badge className="bg-green-600 text-white gap-1">
        <CheckCircle2 className="h-3 w-3" /> Aprobada
      </Badge>
    );
  if (estado === 'rechazada')
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="h-3 w-3" /> Rechazada
      </Badge>
    );
  return <Badge className="bg-amber-500 text-white">Pendiente</Badge>;
};

export const JornadaDetalleCardDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  jornadas,
  canValidate,
  onAprobar,
  onRechazar,
  onReabrir,
  onEditar,
  onEliminar,
  loading,
}) => {
  const [rechazandoId, setRechazandoId] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [fotosAbiertasId, setFotosAbiertasId] = useState<string | null>(null);

  const handleClose = (next: boolean) => {
    if (!next) {
      setRechazandoId(null);
      setMotivo("");
      setFotosAbiertasId(null);
    }
    onOpenChange(next);
  };

  const confirmarRechazo = async (j: JornadaDetalle) => {
    if (!motivo.trim()) return;
    await onRechazar(j, motivo.trim());
    setRechazandoId(null);
    setMotivo("");
  };

  const fechaTitulo =
    jornadas.length > 0 ? format(parseISO(jornadas[0].fecha), "EEEE, dd MMMM yyyy", { locale: es }) : "";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="capitalize">{fechaTitulo}</DialogTitle>
          <DialogDescription>
            {jornadas.length} jornada{jornadas.length !== 1 ? "s" : ""} registrada
            {jornadas.length !== 1 ? "s" : ""} este día
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {jornadas.map((j) => {
            const enRechazo = rechazandoId === j.id;
            const tipoColor = getWorkTypeColor(j.tipo_trabajo);
            return (
              <div key={j.id} className="border rounded-lg p-4 bg-card space-y-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4 text-muted-foreground" />
                      {j.tecnico?.nombre || "—"}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      {j.hora_inicio?.slice(0, 5)}
                      {j.hora_fin ? ` – ${j.hora_fin.slice(0, 5)}` : " · En curso"}
                      {j.horas_trabajadas != null && (
                        <span className="font-medium text-foreground">
                          · {j.horas_trabajadas.toFixed(2)} h
                        </span>
                      )}
                      {j.es_hora_extra && (
                        <Badge className="bg-orange-500 text-white text-[10px] h-4 px-1.5">Extra</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cn("text-xs", tipoColor)}>
                      {getWorkTypeLabel(j.tipo_trabajo)}
                    </Badge>
                    <EstadoBadge estado={j.estado_validacion} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {j.obra?.nombre && (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Building2 className="h-3.5 w-3.5" />
                      <span className="truncate">{j.obra.nombre}</span>
                    </div>
                  )}
                  {j.orden_trabajo && (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <ClipboardList className="h-3.5 w-3.5" />
                      <span className="truncate">
                        {j.orden_trabajo.numero_ot} · {j.orden_trabajo.titulo}
                      </span>
                    </div>
                  )}
                  {j.cliente?.nombre && !j.obra && !j.orden_trabajo && (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Building2 className="h-3.5 w-3.5" />
                      <span className="truncate">{j.cliente.nombre}</span>
                    </div>
                  )}
                </div>

                {j.descripcion_trabajo && (
                  <p className="text-sm text-foreground whitespace-pre-wrap">{j.descripcion_trabajo}</p>
                )}

                {j.estado_validacion === 'rechazada' && j.motivo_rechazo && (
                  <div className="text-xs rounded-md border border-destructive/30 bg-destructive/5 text-destructive p-2">
                    <strong>Motivo de rechazo:</strong> {j.motivo_rechazo}
                  </div>
                )}

                <div className="pt-2 border-t">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 h-8 px-2 text-xs"
                    onClick={() => setFotosAbiertasId(prev => prev === j.id ? null : j.id)}
                  >
                    <Camera className="h-3.5 w-3.5" />
                    {fotosAbiertasId === j.id ? 'Ocultar fotos y documentos' : 'Ver fotos y documentos'}
                    {fotosAbiertasId === j.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </Button>
                  {fotosAbiertasId === j.id && (
                    <div className="mt-2 rounded-md border bg-muted/30 p-2">
                      <JornadaDocumentos jornadaId={j.id} readonly />
                    </div>
                  )}
                </div>


                {canValidate && (
                  <>
                    {enRechazo ? (
                      <div className="space-y-2 pt-2 border-t">
                        <Textarea
                          placeholder="Motivo del rechazo (visible para el técnico)..."
                          value={motivo}
                          onChange={(e) => setMotivo(e.target.value)}
                          rows={2}
                        />
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setRechazandoId(null);
                              setMotivo("");
                            }}
                            disabled={loading}
                          >
                            Cancelar
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => confirmarRechazo(j)}
                            disabled={loading || !motivo.trim()}
                          >
                            Confirmar rechazo
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 justify-end pt-2 border-t flex-wrap">
                        {onEditar && j.estado_validacion !== 'aprobada' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() => onEditar(j)}
                            disabled={loading}
                          >
                            <Pencil className="h-4 w-4" /> Editar
                          </Button>
                        )}
                        {onEliminar && j.estado_validacion === 'rechazada' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-destructive border-destructive/40 hover:bg-destructive/10"
                            onClick={() => onEliminar(j)}
                            disabled={loading}
                          >
                            <Trash2 className="h-4 w-4" /> Eliminar
                          </Button>
                        )}
                        {j.estado_validacion === 'aprobada' && onReabrir && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() => onReabrir(j)}
                            disabled={loading}
                            title="Vuelve a 'pendiente' para poder corregir"
                          >
                            <Unlock className="h-4 w-4" /> Reabrir para corregir
                          </Button>
                        )}
                        {j.estado_validacion !== 'aprobada' && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white gap-1"
                            onClick={() => onAprobar(j)}
                            disabled={loading}
                          >
                            <Check className="h-4 w-4" /> Aprobar
                          </Button>
                        )}
                        {j.estado_validacion !== 'rechazada' && j.estado_validacion !== 'aprobada' && (
                          <Button
                            size="sm"
                            variant="destructive"
                            className="gap-1"
                            onClick={() => {
                              setRechazandoId(j.id);
                              setMotivo("");
                            }}
                            disabled={loading}
                          >
                            <X className="h-4 w-4" /> Rechazar
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
